import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  Platform,
  ScrollView,
  Alert,
  Image,
  ActivityIndicator,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { getColor, spacing, getBorderRadius, getShadow } from '../../theme/useTheme';
import { H1, H2, Body1, Caption, ButtonText } from '../../components/common/TextStyles';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AdminLayout from '../components/layout/AdminLayout';
import { createEvent, updateEvent, uploadEventImage } from '../api/events';
import { getImageUrl } from '../../utils/imageUtils';

const VIBES = ['Skill Up', 'Live Talks', 'Seminar', 'Meetups'];

const EventAddScreen = ({ route, navigation }) => {
  const insets = useSafeAreaInsets();
  const editEvent = route.params?.editEvent || null;

  const [title, setTitle] = useState(editEvent?.title || '');
  const [description, setDescription] = useState(editEvent?.description || '');
  const [vibe, setVibe] = useState(editEvent?.vibe || VIBES[0]);
  const [type, setType] = useState(editEvent?.type || 'in_person');
  const [locationName, setLocationName] = useState(editEvent?.location_name || '');
  const [virtualLink, setVirtualLink] = useState(editEvent?.virtual_link || '');
  const [imageUrl, setImageUrl] = useState(editEvent?.image_url || '');
  const [pointsReward, setPointsReward] = useState(editEvent?.points_reward?.toString() || '15');
  const [isUploading, setIsUploading] = useState(false);

  const [date, setDate] = useState(editEvent?.event_date ? new Date(editEvent.event_date) : new Date());
  const [endDate, setEndDate] = useState(editEvent?.event_end_date ? new Date(editEvent.event_end_date) : new Date());
  const [startTime, setStartTime] = useState(editEvent?.start_time ? new Date(editEvent.start_time) : new Date());
  const [endTime, setEndTime] = useState(editEvent?.end_time ? new Date(editEvent.end_time) : new Date());
  
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const handlePickImage = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Denied', 'We need camera roll permissions to upload a photo.');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [16, 9],
        quality: 0.7,
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        try {
          setIsUploading(true);
          const uploadRes = await uploadEventImage(asset.uri);
          if (uploadRes.success) {
             setImageUrl(uploadRes.imageUrl);
          }
        } catch(uploadErr) {
             Alert.alert('Upload Failed', uploadErr.message);
        } finally {
          setIsUploading(false);
        }
      }
    } catch (error) {
      console.error('Pick image error:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!title.trim() || !description.trim()) {
        Alert.alert('Error', 'Please provide a title and description.');
        return;
      }

      const combineDateAndTime = (baseDate, timeDate) => {
        const combined = new Date(baseDate);
        combined.setHours(timeDate.getHours());
        combined.setMinutes(timeDate.getMinutes());
        combined.setSeconds(0);
        combined.setMilliseconds(0);
        return combined;
      };

      const eventData = {
        title: title.trim(),
        description: description.trim(),
        event_date: date.toISOString(),
        event_end_date: endDate.toISOString(),
        start_time: combineDateAndTime(date, startTime).toISOString(),
        end_time: combineDateAndTime(endDate, endTime).toISOString(),
        vibe,
        type,
        location_name: type === 'in_person' ? locationName.trim() : null,
        virtual_link: type === 'virtual' ? virtualLink.trim() : null,
        image_url: imageUrl.trim() || null,
        points_reward: parseInt(pointsReward) || 0
      };

      if (editEvent) {
        await updateEvent(editEvent.id, eventData);
        Alert.alert('Success', 'Event updated successfully');
      } else {
        await createEvent(eventData);
        Alert.alert('Success', 'Event created successfully');
      }
      
      navigation.goBack();
    } catch (error) {
      console.error('Error adding/updating event:', error);
      Alert.alert('Error', error.message || 'Failed to save event');
    }
  };

  const formatDateForDisplay = (dateToFormat) => {
    return dateToFormat.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTimeForDisplay = (dateToFormat) => {
    return dateToFormat.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderDatePicker = () => {
    if (Platform.OS === 'web') {
      return (
        <View style={{ gap: 10 }}>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <Caption style={[styles.label, { marginBottom: 4 }]}>From Date</Caption>
              <input
                type="date"
                value={date.toISOString().split('T')[0]}
                onChange={(e) => setDate(new Date(e.target.value))}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 1.5,
                  borderColor: '#e2e8f0',
                  backgroundColor: '#FFFFFF',
                  fontSize: 14,
                  width: '100%',
                }}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Caption style={[styles.label, { marginBottom: 4 }]}>Till Date</Caption>
              <input
                type="date"
                value={endDate.toISOString().split('T')[0]}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 1.5,
                  borderColor: '#e2e8f0',
                  backgroundColor: '#FFFFFF',
                  fontSize: 14,
                  width: '100%',
                }}
              />
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <Caption style={[styles.label, { marginBottom: 4 }]}>From</Caption>
              <input
                type="time"
                value={`${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':');
                  const newTime = new Date(startTime);
                  newTime.setHours(parseInt(h), parseInt(m));
                  setStartTime(newTime);
                }}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 1.5,
                  borderColor: '#e2e8f0',
                  backgroundColor: '#FFFFFF',
                  fontSize: 14,
                  width: '100%',
                }}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Caption style={[styles.label, { marginBottom: 4 }]}>To</Caption>
              <input
                type="time"
                value={`${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':');
                  const newTime = new Date(endTime);
                  newTime.setHours(parseInt(h), parseInt(m));
                  setEndTime(newTime);
                }}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 1.5,
                  borderColor: '#e2e8f0',
                  backgroundColor: '#FFFFFF',
                  fontSize: 14,
                  width: '100%',
                }}
              />
            </View>
          </View>
        </View>
      );
    }

    return (
      <View>
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <View style={{ flex: 1 }}>
            <Caption style={styles.label}>From Date</Caption>
            <TouchableOpacity
              style={styles.mobileDataSelector}
              onPress={() => setShowDatePicker(true)}
            >
              <Ionicons name="calendar-outline" size={20} color="#6366F1" style={{ marginRight: 10 }} />
              <Text style={styles.dateText}>{formatDateForDisplay(date)}</Text>
            </TouchableOpacity>
          </View>

          <View style={{ flex: 1 }}>
            <Caption style={styles.label}>Till Date</Caption>
            <TouchableOpacity
              style={styles.mobileDataSelector}
              onPress={() => setShowEndDatePicker(true)}
            >
              <Ionicons name="calendar-outline" size={20} color="#6366F1" style={{ marginRight: 10 }} />
              <Text style={styles.dateText}>{formatDateForDisplay(endDate)}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {showDatePicker && (
          <DateTimePicker
            testID="dateTimePicker"
            value={date}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowDatePicker(Platform.OS === 'ios');
              if (selectedDate) setDate(selectedDate);
            }}
          />
        )}

        {showEndDatePicker && (
          <DateTimePicker
            testID="endDateTimePicker"
            value={endDate}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowEndDatePicker(Platform.OS === 'ios');
              if (selectedDate) setEndDate(selectedDate);
            }}
          />
        )}

        <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
           <TouchableOpacity
            style={[styles.mobileDataSelector, { flex: 1 }]}
            onPress={() => setShowStartTimePicker(true)}
          >
            <Ionicons name="time-outline" size={18} color="#6366F1" style={{ marginRight: 8 }} />
            <Text style={styles.dateText}>From: {formatTimeForDisplay(startTime)}</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.mobileDataSelector, { flex: 1 }]}
            onPress={() => setShowEndTimePicker(true)}
          >
            <Ionicons name="time-outline" size={18} color="#6366F1" style={{ marginRight: 8 }} />
            <Text style={styles.dateText}>To: {formatTimeForDisplay(endTime)}</Text>
          </TouchableOpacity>
        </View>

        {showStartTimePicker && (
          <DateTimePicker
            testID="startTimePicker"
            value={startTime}
            mode="time"
            is24Hour={false}
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowStartTimePicker(Platform.OS === 'ios');
              if (selectedDate) setStartTime(selectedDate);
            }}
          />
        )}

        {showEndTimePicker && (
          <DateTimePicker
            testID="endTimePicker"
            value={endTime}
            mode="time"
            is24Hour={false}
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowEndTimePicker(Platform.OS === 'ios');
              if (selectedDate) setEndTime(selectedDate);
            }}
          />
        )}
      </View>
    );
  };

  return (
    <AdminLayout>
      <View style={{ flex: 1, backgroundColor: '#f8fafc', paddingTop: insets.top }}>
        <View style={styles.header}>
          <View>
            <H2 style={styles.headerTitle}>{editEvent ? 'Edit Activity' : 'New Activity'}</H2>
            <Caption style={{ color: '#94a3b8' }}>Setup event details</Caption>
          </View>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.closeButton}>
            <Ionicons name="close" size={22} color="#64748b" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
          <View style={styles.content}>
            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Activity Title</Caption>
              <TextInput
                style={styles.input}
                value={title}
                onChangeText={setTitle}
                placeholder="e.g., Tech Startup Pitch Night"
                placeholderTextColor="#94a3b8"
              />
            </View>

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Description</Caption>
              <TextInput
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={description}
                onChangeText={setDescription}
                placeholder="Event details..."
                placeholderTextColor="#94a3b8"
              />
            </View>

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Timeline</Caption>
              {renderDatePicker()}
            </View>

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Activity Vibe</Caption>
              <View style={styles.typeSelector}>
                {VIBES.map((v) => (
                  <TouchableOpacity
                    key={v}
                    style={[styles.typeButton, vibe === v && styles.typeButtonActive]}
                    onPress={() => setVibe(v)}
                  >
                    <Text style={[styles.typeText, vibe === v && styles.typeTextActive]}>{v}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Type</Caption>
              <View style={styles.typeSelector}>
                <TouchableOpacity
                  style={[styles.typeButton, type === 'in_person' && styles.typeButtonActive]}
                  onPress={() => setType('in_person')}
                >
                  <Text style={[styles.typeText, type === 'in_person' && styles.typeTextActive]}>In Person</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.typeButton, type === 'virtual' && styles.typeButtonActive]}
                  onPress={() => setType('virtual')}
                >
                  <Text style={[styles.typeText, type === 'virtual' && styles.typeTextActive]}>Virtual</Text>
                </TouchableOpacity>
              </View>
            </View>

            {type === 'in_person' ? (
              <View style={styles.inputGroup}>
                <Caption style={styles.label}>Location</Caption>
                <TextInput
                  style={styles.input}
                  value={locationName}
                  onChangeText={setLocationName}
                  placeholder="e.g., iHub Nairobi"
                  placeholderTextColor="#94a3b8"
                />
              </View>
            ) : (
              <View style={styles.inputGroup}>
                <Caption style={styles.label}>Access Link</Caption>
                <TextInput
                  style={styles.input}
                  value={virtualLink}
                  onChangeText={setVirtualLink}
                  placeholder="e.g., https://zoom.us/j/..."
                  placeholderTextColor="#94a3b8"
                />
              </View>
            )}

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Media Asset (Optional)</Caption>
              
              <View style={styles.imageUploadContainer}>
                {imageUrl ? (
                   <View style={styles.previewContainer}>
                       <Image 
                         source={{ uri: getImageUrl(imageUrl) }} 
                         style={styles.imagePreview} 
                       />
                       <TouchableOpacity style={styles.removeImageBtn} onPress={() => setImageUrl('')}>
                          <Ionicons name="close-circle" size={20} color="#EF4444" />
                       </TouchableOpacity>
                   </View>
                ) : (
                  <TouchableOpacity 
                    style={styles.uploadBox} 
                    onPress={handlePickImage}
                    disabled={isUploading}
                  >
                     {isUploading ? (
                       <ActivityIndicator size="small" color="#6366F1" />
                     ) : (
                       <View style={styles.uploadContent}>
                         <Ionicons name="image-outline" size={28} color="#94a3b8" />
                         <Text style={styles.uploadText}>Upload Image</Text>
                       </View>
                     )}
                  </TouchableOpacity>
                )}
              </View>

              <TextInput
                style={[styles.input, { marginTop: 10, fontSize: 12, padding: 8 }]}
                value={imageUrl}
                onChangeText={setImageUrl}
                placeholder="Or paste media URL..."
                placeholderTextColor="#94a3b8"
              />
            </View>

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>XP Reward</Caption>
              <TextInput
                style={styles.input}
                value={pointsReward}
                onChangeText={setPointsReward}
                keyboardType="numeric"
                placeholderTextColor="#94a3b8"
              />
            </View>
          </View>
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, 20) }]}>
          <TouchableOpacity
            style={styles.submitButton}
            onPress={handleSubmit}
          >
            <ButtonText style={styles.submitButtonText}>{editEvent ? 'Update' : 'Deploy'}</ButtonText>
          </TouchableOpacity>
        </View>
      </View>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    backgroundColor: '#FFFFFF',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#0f172a',
  },
  content: {
    padding: 20,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f1f5f9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    marginBottom: 6,
    fontWeight: '700',
    fontSize: 11,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    color: '#1e293b',
    backgroundColor: '#FFFFFF',
  },
  imageUploadContainer: {
    marginTop: 4,
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    borderStyle: 'dashed',
    borderRadius: 12,
    backgroundColor: '#f8fafc',
    overflow: 'hidden',
    height: 120,
  },
  uploadBox: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadContent: {
    alignItems: 'center',
  },
  uploadText: {
    marginTop: 6,
    color: '#64748b',
    fontSize: 12,
    fontWeight: '600',
  },
  previewContainer: {
    flex: 1,
    position: 'relative',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  removeImageBtn: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 15,
  },
  textArea: {
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    padding: 12,
    minHeight: 80,
    fontSize: 14,
    textAlignVertical: 'top',
    color: '#1e293b',
    backgroundColor: '#FFFFFF',
  },
  typeSelector: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  typeButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    backgroundColor: '#FFFFFF',
  },
  typeButtonActive: {
    backgroundColor: '#6366F1',
    borderColor: '#6366F1',
  },
  typeText: {
    color: '#64748b',
    fontWeight: '600',
    fontSize: 12,
  },
  typeTextActive: {
    color: '#FFFFFF',
  },
  footer: {
    padding: 20,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
  },
  submitButton: {
    backgroundColor: '#6366F1',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    ...getShadow('sm'),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 14,
  },
  mobileDataSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    padding: 12,
  },
  dateText: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '600',
  },
});

export default EventAddScreen;
